import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { ShoppingBag, Building2, Car, Zap, MapPin, TrendingUp } from 'lucide-react';

export default function FacilitiesSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  const { t } = useLanguage();

  const facilities = [
    { icon: ShoppingBag, title: t('facilities.retailShops'), desc: t('facilities.retailShopsDesc') },
    { icon: Building2, title: t('facilities.officeSpaces'), desc: t('facilities.officeSpacesDesc') },
    { icon: Car, title: t('facilities.parking'), desc: t('facilities.parkingDesc') },
    { icon: Zap, title: t('facilities.powerBackup'), desc: t('facilities.powerBackupDesc') },
    { icon: MapPin, title: t('facilities.primeLocation'), desc: t('facilities.primeLocationDesc') },
    { icon: TrendingUp, title: t('facilities.highFootfall'), desc: t('facilities.highFootfallDesc') },
  ];

  return (
    <section id="facilities" data-testid="facilities-section" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0F172A]">
      <div className="max-w-7xl mx-auto" ref={ref}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] font-bold mb-4 font-['Outfit']">{t('facilities.label')}</p>
          <h2 className="text-3xl sm:text-4xl font-light text-[#F8FAFC] font-['Cormorant_Garamond'] tracking-tight">{t('facilities.title')}</h2>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {facilities.map((item, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: i * 0.1 }} data-testid={`facility-card-${i}`} className="group bg-[#0B1120] border border-white/5 rounded-lg p-8 hover:border-[#D4AF37]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#D4AF37]/5">
              <div className="w-12 h-12 bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-sm flex items-center justify-center mb-5 group-hover:bg-[#D4AF37]/20 group-hover:border-[#D4AF37]/40 transition-all duration-300">
                <item.icon className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="text-xl font-medium text-[#F8FAFC] font-['Cormorant_Garamond'] mb-2">{item.title}</h3>
              <p className="text-sm text-[#94A3B8] font-['Outfit'] leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
